<script>
import {
  mapState,
  mapMutations,
  mapGetters,
} from 'vuex'

import api      from './api'
import Helper   from './components/Helper'
import Quadrant from './components/Quadrant'
import MapIntro from './components/Utils/MapIntro'
import MedIntro from './components/Utils/MedIntro'

export default {

  name: 'App',

  components: {
    Helper,
    Quadrant,
    MapIntro,
    MedIntro
  },

  data() {
    return {
      routing         : false,
      handler         : null,
      desiresMapIntro : false,
      desiresMedIntro : false,
      desiresNav      : false,
      desiresAccess   : false,
    }
  },

  computed: {

    ...mapState([
      'isMobile',
      'location',
      'quadrants',
    ]),
    ...mapState('access', [
      'interacted',
      'visited',
      'desiresInlay',
      'options'
    ]),
    ...mapState('styles',[
      'styles'
    ]),
    ...mapGetters([
      'quadrantBySlug'
    ]),

  },

  async created() {

    console.log(this.$map)

    this.setMobile(this.checkIfMobile())
    window.onresize = () => {
      this.setMobile(this.checkIfMobile())
      this.route(this.$route.path, 'auto')
    }

    this.$router.beforeEach(to => {
      const slug = to.path.replace(this.$baseURL, '').replace('/', '')
      console.log(slug)
      if ( slug == 'inlay') {
         return {
          path: 'map',
          query: {
            inlay: 'physical'
          }
        }
      } else if (!slug) {
        console.log('Redirecting to map quadrant')
        return {
          path: 'map',
          query: {
            inlay: this.desiresInlay ? undefined : 'physical'
          }
        }
      } else if (slug && !this.quadrantBySlug(slug)) {
        return '404'
      }
    })

    this.$router.afterEach(to => {
      if (to.query && to.query.inlay) {
        if (to.query.inlay == 'physical') {
          this.toggleInlay(false)
        }
      }
      this.route(to.path, 'smooth')
    })

    this.$store.dispatch('access/loadOptionsFromStore')

    this.setSnippets( await api.snippets.getAll() )
    this.setAnchors( await api.anchors.getAll() )

  },

  mounted() {

    this.route(window.location.pathname, 'auto')

    if (!this.visited) {
      setTimeout(() => {
        this.desiresMapIntro = true
        this.desiresMedIntro = true
      }, 1000)
    }

    this.handler = () => {
      this.setInteracted()
      document.removeEventListener('mousedown', this.handler, false)
    }
    document.addEventListener('mousedown', this.handler)

    document.addEventListener('keydown', e => {
      if (!this.interacted) {
        this.setInteracted()
      }
      if (e.key == 'a') {
        this.desiresAccess = !this.desiresAccess
      } else if (e.key == 'n') {
        this.desiresNav = !this.desiresNav
      }
    })

  },

  methods: {

    ...mapMutations([
      'setMobile',
      'setLocation',
      'setSnippets',
      'setAnchors',
      'setHelper',
    ]),
    ...mapMutations('access', [
      'setInteracted',
      'toggleInlay'
    ]),

    log: a => console.log(a),

    checkIfMobile: () => window.innerWidth <= 700,

    route(path, behavior) {

      const slug = path.replace(this.$baseURL, '').replace('/', '')
        || this.quadrants.map && this.quadrants.map.slug
        || 'map'

      this.setLocation(slug)
      this.routing = true
      setTimeout(() => {
        if (this.routing) {
          this.scroll({
            top  : this.$refs[slug][0].$el.offsetTop,
            left : this.$refs[slug][0].$el.offsetLeft,
          }, behavior)
          this.setHelper(this.getHelperPosition(slug))
        }
      }, 100)

    },

    scroll({ top, left }, behavior ) {
      this.$el.scroll({ top, left, behavior })
      setTimeout(() => {
        this.routing = false
      }, 700)
    },

    getHelperPosition(slug) {
      let gap = { top: 0, left: 0 }
      if (slug == 'ice') {
        if (this.isMobile) { gap = {
          top: window.innerHeight - 100,
          left: 40,
        }} else { gap = {
          top: window.innerHeight - 130,
          left: 30,
        }}
      } else { gap = {
        top: window.innerHeight - 50,
        left: 50,
      }}
      return gap
    }
  }
}
</script>

<template>
  <div
    lang="en"
    id="app"
    :class="{
      isMobile,
      routing,
      highContrast: true,
      [`route-${location}`]: true
    }"
    :style="{
      '--dark1'      : styles.dark1,
      '--light1'     : styles.light1,
      '--dark2'      : styles.dark2,
      '--light2'     : styles.light2,
      '--dark3'      : styles.dark3,
      '--light3'     : styles.light3,
      '--sizeFactor' : options.fontSize.value,
    }"
    @keydown.tab="log($event.target.getAttribute('aria-label'))"
  >

    <main
      aria-label="Main application"
    >

      <Quadrant
        v-for="quadrant in quadrants"
        :key="quadrant.slug"
        :ref="quadrant.slug"
        :quadrant="quadrant"
        :desiresMed="!desiresMedIntro"
      />

    </main>

    <Helper
      :desiresNav="desiresNav"
      :desiresAccess="desiresAccess"
    />

    <transition
      name="fade"
      mode="out-in"
    >
      <MapIntro
        v-if="!isMobile && desiresMapIntro && location == 'map'"
        @endIntro="desiresMapIntro = false"
      />
      <MedIntro
        v-else-if="desiresMedIntro && location == 'meditation'"
        @endIntro="desiresMedIntro = false"
      />
    </transition>

  </div>
</template>

<style>

@font-face {
  font-family: 'Space Grotesk';
  src: url('./assets/fonts/space-grotesk/Regular.woff2');
  font-style: normal;
  font-weight: normal;
}
@font-face {
  font-family: 'Space Grotesk';
  src: url('./assets/fonts/space-grotesk/Bold.woff2');
  font-style: normal;
  font-weight: bold;
}





:root {

  --background: rgb(3, 29, 11);
  /* --highlight: rgb(255, 255, 0); */
  --copy: rgb(170, 170, 170);

  /* --mesh:
    radial-gradient(at 40% 20%, hsla(230,86%,6%,1) 0, transparent 50%),
    radial-gradient(at 0% 50%, hsla(182,48%,15%,1) 0, transparent 50%),
    radial-gradient(at 80% 50%, hsla(310,100%,13%,1) 0, transparent 50%),
    radial-gradient(at 0% 100%, hsla(323,100%,5%,1) 0, transparent 50%),
    radial-gradient(at 80% 100%, hsla(272,97%,12%,1) 0, transparent 50%),
    radial-gradient(at 0% 0%, hsla(60,39%,14%,1) 0, transparent 50%)
  ; */

  --mesh:
    radial-gradient(at 40% 20%, var(--dark1) 0, transparent 50%),
    radial-gradient(at 0% 50%, var(--dark2) 0, transparent 50%),
    radial-gradient(at 80% 50%, var(--dark1) 0, transparent 50%),
    radial-gradient(at 0% 100%, var(--dark2) 0, transparent 50%),
    radial-gradient(at 80% 100%, var(--dark1) 0, transparent 50%),
    radial-gradient(at 0% 0%, var(--dark2) 0, transparent 50%)
  ;

  --veryfast: 0.1s;
  --fast: 0.2s;
  --slow: 0.5s;
  --veryslow: 2s;
  --landing: 1s;

}

html,
body,
#app {
  margin: 0; padding: 0;
  height: 100%; width: 100%;
  font-family: 'Space Grotesk', Helvetica, sans-serif;
  color: var(--copy);
  /* background: var(--background); */
  background-color: var(--dark1);
  /* background-image: var(--mesh); */
  overflow: hidden;
  /* transition: all 0.5s ease; */
  transition:
    background-color var(--slow) ease,
    color var(--slow) ease
  ;
}

#app {
  position: relative;

  --copy: var(--light1);
  --highlight: var(--dark1);
  --back: var(--dark1)

}

#app.routing {
  overflow: scroll;
}


h1,
h2,
p {
  line-height: 1.2;
  color: var(--light1);
}
h2 {
  /* font-size: 25px; */
  font-size: calc(25px * var(--sizeFactor));
}
p,span, a {
  /* font-size: 18px; */
  font-size: calc(18px * var(--sizeFactor));
}
h1::selection,
h2::selection,
h3::selection,
h4::selection,
h5::selection,
h6::selection,
 p::selection {
  background: var(--light2);
  color: var(--light1);
}
a,
a:visited,
a:active {
  color: var(--light2);
}

main {
  position: relative;
  height: 300%;
  width: 300%;
  display: block;
  overflow: scroll;
  /* background-image:
    radial-gradient(at 40% 20%, var(--dark1) 0, transparent 50%),
    radial-gradient(at 0% 50%, var(--dark2) 0, transparent 50%),
    radial-gradient(at 80% 50%, var(--dark1) 0, transparent 50%),
    radial-gradient(at 0% 100%, var(--dark2) 0, transparent 50%),
    radial-gradient(at 80% 100%, var(--dark1) 0, transparent 50%),
    radial-gradient(at 0% 0%, var(--dark2) 0, transparent 50%)
  ; */
  background-size: 100% 100%;
  /* background-position: -150% -150%; */
}

.ui {
  position: relative;
  padding: 1em;
  border-radius: 1em;
  user-select: none;
  /* font-size: 16pt; */
  font-size: calc(16pt * var(--sizeFactor));
  color: var(--back);
  transition:
    filter var(--veryslow) ease,
    transform var(--slow) ease,
  ;
  z-index: 0;
  /* backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  transform:translate3d(0,0,0);
  -webkit-transform:translate3d(0,0,0);
  transform-style: preserve-3d;
  -webkit-transform-style: preserve-3d;
  perspective: 1000;
  -webkit-perspective: 1000; */
}

.ui::before,
.ui::after {
  content: '';
  position: absolute;
  z-index: -1;
  filter: blur(15px);
}

.ui::before {
  top: -5%;
  left: -5%;
  width: 110%;
  height: 110%;
  opacity: 0.7;
  animation: morph 8s ease-in-out alternate infinite;
  background-color: var(--light2);
  box-shadow: 0 0 40px 20px var(--light2);
  transition:
    filter var(--slow) ease,
    transform var(--slow) ease,
    opacity var(--slow) ease,
    background-color var(--slow) ease,
    box-shadow var(--slow) ease
  ;
}

.ui::after {
  top: 0%;
  left: 0%;
  width: 100%;
  height: 100%;
  opacity: 0.65;
  animation: morph2 8s ease-in-out alternate infinite;
  background-color: var(--light1);
  box-shadow: 0 0 50px 20px var(--light1);
  transition:
    filter var(--veryslow) ease,
    transform var(--veryslow) ease,
    opacity var(--veryslow) ease,
    background var(--veryslow) ease,
    box-shadow var(--veryslow) ease
  ;
}

.routing .ui,
.ui:focus,
.ui:focus-within,
.ui.hover,
.ui:hover {
  filter: drop-shadow(0 0 80px var(--light1));
  transform: scale(0.9);
  transform-origin: center center;
  transition:
    filter var(--veryslow) ease,
    transform var(--slow) ease,
  ;
}
.routing .ui::before,
.ui:focus::before,
.ui:focus-within::before,
.ui.hover::before,
.ui:hover::before,
.routing .ui::after,
.ui:focus::after,
.ui:focus-within::after,
.ui.hover::after,
.ui:hover::after {
  filter: blur(13px);
}
.routing .ui::before,
.ui:focus::before,
.ui:focus-within::before,
.ui.hover::before,
.ui:hover::before {
  transform: scale(0.8);
  opacity: 0.9;
  background-color: var(--light1);
  box-shadow: 0 0 30px 12px var(--light1);
  transition:
    filter var(--veryslow) ease,
    transform var(--veryslow) ease,
    opacity var(--veryslow) ease,
    background var(--veryslow) ease,
    box-shadow var(--veryslow) ease
  ;
}
.routing .ui::after,
.ui:focus::after,
.ui:focus-within::after,
.ui.hover::after,
.ui:hover::after {
  transform: scale(0.75);
  opacity: 0.95;
  background-color: var(--light2);
  box-shadow: 0 0 22px 10px var(--light2);
  transition:
    filter var(--slow) ease,
    transform var(--slow) ease,
    opacity var(--slow) ease,
    background-color var(--slow) ease,
    box-shadow var(--slow) ease
  ;
}

.routing .ui {
  transform: scale(0.5);
}

.ui a,
.button a {
  transition: all var(--fast) ease;
  text-decoration: none;
  cursor: pointer;
  border-radius: 0.2em;
  padding: 0.1em 0.5em;
  color: var(--dark2)
}

.ui a:hover,
.button a:hover,
.ui a.hover,
.ui a:focus {
  background: var(--back);
  color: var(--light2);
}

.fallback {
  font-size: calc(16pt * var(--sizeFactor));
  padding: 2em;

}


.fade-enter-active, .fade-leave-active {
  transition: opacity var(--slow) ease;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

@keyframes morph {
    0% {
      border-radius: 60% 40% 30% 70%/60% 30% 40% 40%;
  }
   50% {
      border-radius: 60% 30% 60% 40%/50% 70% 60% 60%;
  }
  100% {
      border-radius: 60% 40% 30% 70%/60% 30% 40% 60%;
  }
}
@keyframes morph2 {
    0% {
    border-radius: 30% 60% 70% 40%/50% 60% 60% 60%;
  }
   50% {
    border-radius: 60% 40% 30% 70%/60% 30% 40% 40%;
  }
  100% {
    border-radius: 30% 60% 70% 40%/50% 60% 60% 60%;
  }
}
@keyframes fade {
  from { opacity: 0; }
  to { opacity: 1; }
}




.isMobile .ui {
  /* padding: 0.5em; */
  /* font-size: 12pt; */
}
.isMobile .ui::before {
  opacity: 0.8;
  box-shadow: 0 0 20px 10px var(--light2);
}
.isMobile .ui::after {
  opacity: 0.75;
  box-shadow: 0 0 25px 10px var(--light1);
}

.isMobile.routing .ui,
.isMobile .ui.hover,
.isMobile .ui:hover {
  filter: drop-shadow(0 0 40px var(--light1));
}

.isMobile.routing .ui::before,
.isMobile .ui.hover::before,
.isMobile .ui:hover::before {
  opacity: 0.95;
  box-shadow: 0 0 15px 6px var(--light1);
}
.isMobile.routing .ui::after,
.isMobile .ui.hover::after,
.isMobile .ui:hover::after {
  opacity: 1;
  box-shadow: 0 0 11px 5px var(--light2);
}
/* .isMobile .ui a {
  border-radius: 0.2em;
  padding: 0.1em 0.5em;
} */

#app.highContrast {
  /* --light1: rgb(230, 230, 230) !important; */
  /* --light2: white !important; */
}
#app.highContrast .ui,
#app.highContrast .ui::before,
#app.highContrast .ui::after {
  box-shadow: none !important;
  filter: none !important;
  opacity: 1 !important;
}

/* #app.highContrast .ui::before,
#app.highContrast .ui::after {
  background: black;
  border: 2px solid white;
}

#app.highContrast .ui a {
  color: white;
} */

#app.highContrast .quadrant {
  filter: blur(0) !important;
  transform: unset !important;
}

#app.route-meditation #showEverything {
  display: none;
}

</style>
